import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { cn } from '../utils/cn.js';
import { Input } from './Input.js';
// useLayoutEffect warns (and does nothing) during SSR. This component can be used
// inside Astro server-rendered islands, so fall back to useEffect on the server —
// the caret-restore effect is client-only anyway. (Mirrors DateInput.)
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;
const MONTH_RE = /^\d{4}-\d{2}$/;
/** True when `v` is a complete, real year-month in YYYY-MM. */
export function isValidYm(v) {
    if (!MONTH_RE.test(v))
        return false;
    const [y, m] = v.split('-').map(Number);
    return y >= 1900 && y <= 9999 && m >= 1 && m <= 12;
}
/** Mask an arbitrary string to at most `YYYY-MM`, deriving the dash from digits. */
function maskYm(raw) {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    const parts = [digits.slice(0, 4)];
    if (digits.length > 4)
        parts.push(digits.slice(4, 6));
    return parts.join('-');
}
/** Count digits in `s` up to string index `end`. */
function digitsBefore(s, end) {
    return s.slice(0, end).replace(/\D/g, '').length;
}
/** Index in `masked` just after the `n`-th digit (for caret restoration). */
function caretAfterDigits(masked, n) {
    if (n <= 0)
        return 0;
    let seen = 0;
    for (let i = 0; i < masked.length; i++) {
        if (/\d/.test(masked[i])) {
            seen++;
            if (seen === n)
                return i + 1;
        }
    }
    return masked.length;
}
export const MonthInput = forwardRef(function MonthInput({ value, onChange, invalid, className, disabled, ...rest }, ref) {
    const [display, setDisplay] = useState(value);
    const lastEmitted = useRef(value);
    const textRef = useRef(null);
    const pickerRef = useRef(null);
    const pendingCaret = useRef(null);
    // Resync from the parent only when it changed to something we didn't emit
    // (e.g. a form reset), so echoing our '' back for a partial never erases it.
    useEffect(() => {
        if (value !== lastEmitted.current) {
            setDisplay(value);
            lastEmitted.current = value;
        }
    }, [value]);
    // Restore the caret after a mask reformat inserts/removes the dash.
    useIsomorphicLayoutEffect(() => {
        if (pendingCaret.current !== null && textRef.current) {
            const pos = pendingCaret.current;
            textRef.current.setSelectionRange(pos, pos);
            pendingCaret.current = null;
        }
    }, [display]);
    const emit = (next) => {
        const out = isValidYm(next) ? next : '';
        lastEmitted.current = out;
        onChange(out);
    };
    const handleChange = (e) => {
        const raw = e.target.value;
        const caret = e.target.selectionStart ?? raw.length;
        const masked = maskYm(raw);
        pendingCaret.current = caretAfterDigits(masked, digitsBefore(raw, caret));
        setDisplay(masked);
        emit(masked);
    };
    const handlePick = (e) => {
        const v = e.target.value; // native month input → YYYY-MM or ''
        setDisplay(v);
        lastEmitted.current = v;
        onChange(v);
    };
    const openPicker = () => {
        const el = pickerRef.current;
        if (!el)
            return;
        try {
            el.showPicker();
        }
        catch {
            // showPicker() throws if unsupported / not user-activated — the text input
            // remains fully usable, so this is a no-op fallback.
        }
    };
    return (_jsxs("div", { className: cn('relative', className), children: [_jsx(Input, { ref: (node) => {
                    textRef.current = node;
                    if (typeof ref === 'function')
                        ref(node);
                    else if (ref)
                        ref.current = node;
                }, type: "text", inputMode: "numeric", autoComplete: "off", placeholder: "YYYY-MM", maxLength: 7, value: display, onChange: handleChange, invalid: invalid, disabled: disabled, className: "pr-10", ...rest }), _jsx("input", { ref: pickerRef, type: "month", tabIndex: -1, "aria-hidden": "true", value: isValidYm(display) ? display : '', onChange: handlePick, className: "pointer-events-none absolute h-0 w-0 opacity-0" }), _jsx("button", { type: "button", onClick: openPicker, disabled: disabled, tabIndex: -1, "aria-label": "Open month picker", className: cn('absolute inset-y-0 right-0 flex items-center px-2.5 text-ink-muted', 'hover:text-ink focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'), children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }) })] }));
});
//# sourceMappingURL=MonthInput.js.map